import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import { useToast } from '../../../components/ToastNotification';

const VerseReflectionsViewModal = ({
  verse,
  isOpen,
  onClose,
  className = ''
}) => {
  const { user } = useAuth();
  const { showSuccess, showError } = useToast();
  const [selectedTab, setSelectedTab] = useState('all');
  const [likedReflections, setLikedReflections] = useState(new Set());
  const [flaggedReflections, setFlaggedReflections] = useState(new Set());
  const [likeSaving, setLikeSaving] = useState(null);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedTab('all');
      setLikedReflections(new Set());
      setFlaggedReflections(new Set());
    }
  }, [isOpen]);

  if (!isOpen || !verse) return null;

  // Get reflections from verse data - this comes from the database function
  const rawReflections = verse?.reflections || [];
  
  // Transform database reflection format to match component expectations
  const transformReflection = (dbReflection) => {
    return {
      id: dbReflection?.id,
      author: dbReflection?.user_name,
      createdAt: dbReflection?.created_at,
      isPrivate: false, // Database function only returns public reflections
      likes: dbReflection?.popularity_score || 0,
      userHasLiked: false,
      userHasFlagged: false,
      insights: dbReflection?.insights,
      actions: dbReflection?.actions,
      relationships: dbReflection?.relationships,
      testimonies: dbReflection?.testimonies,
      illustrations: dbReflection?.illustrations
    };
  };

  const allReflections = rawReflections?.map(transformReflection) || [];
  const privateReflections = [];
  const publicReflections = allReflections;

  const getDisplayedReflections = () => {
    switch (selectedTab) {
      case 'private': return privateReflections;
      case 'public': return publicReflections;
      default: return allReflections;
    }
  };

  const displayedReflections = getDisplayedReflections();

  const formatDate = (dateString) => {
    try {
      return new Date(dateString)?.toLocaleDateString('en-US', {
        year: 'numeric', month: 'short', day: 'numeric'
      });
    } catch {
      return 'Unknown date';
    }
  };

  const getHeatmapBadge = (reflectionCount) => {
    if (reflectionCount === 0) return { label: 'No reflections', color: 'bg-gray-100 text-gray-600' };
    if (reflectionCount <= 2) return { label: 'Few reflections', color: 'bg-blue-100 text-blue-700' };
    if (reflectionCount <= 5) return { label: 'Some reflections', color: 'bg-green-100 text-green-700' };
    if (reflectionCount <= 10) return { label: 'Many reflections', color: 'bg-yellow-100 text-yellow-700' };
    if (reflectionCount <= 20) return { label: 'Very popular', color: 'bg-orange-100 text-orange-700' };
    return { label: 'Extremely popular', color: 'bg-red-100 text-red-700' };
  };

  const prompts = [
    { id: 'insights', title: 'Personal Insights', icon: 'Lightbulb' },
    { id: 'actions', title: 'Holy Spirit Impressions', icon: 'Zap' },
    { id: 'relationships', title: 'Christ & Church Connection', icon: 'Heart' },
    { id: 'illustrations', title: 'Nature & History Insights', icon: 'TreePine' },
    { id: 'testimonies', title: 'Personal Testimony', icon: 'MessageCircle' }
  ];

  const getPromptInfo = (promptId) => {
    return prompts?.find(p => p?.id === promptId) || { title: promptId, icon: 'BookOpen' };
  };

  // Handle like toggle — persisted to Supabase
  const handleLikeReflection = async (reflectionId) => {
    if (!user?.id) {
      showError('Please sign in to like reflections.');
      return;
    }
    setLikeSaving(reflectionId);
    const isCurrentlyLiked = likedReflections?.has(reflectionId);
    const newLiked = new Set(likedReflections);
    if (isCurrentlyLiked) {
      newLiked?.delete(reflectionId);
    } else {
      newLiked?.add(reflectionId);
    }
    setLikedReflections(newLiked);

    try {
      if (isCurrentlyLiked) {
        await supabase?.from('verse_reflection_likes')?.delete()
          ?.eq('user_id', user?.id)?.eq('reflection_id', reflectionId);
      } else {
        await supabase?.from('verse_reflection_likes')?.upsert(
          { user_id: user?.id, reflection_id: reflectionId },
          { onConflict: 'user_id,reflection_id' }
        );
      }
    } catch (err) {
      // Revert optimistic update on error
      setLikedReflections(likedReflections);
      console.error('Error toggling like:', err);
    } finally {
      setLikeSaving(null);
    }
  };

  // Handle flag — persisted to Supabase
  const handleFlagReflection = async (reflectionId) => {
    if (!user?.id) {
      showError('Please sign in to flag reflections.');
      return;
    }
    const isCurrentlyFlagged = flaggedReflections?.has(reflectionId);
    const newFlagged = new Set(flaggedReflections);
    if (isCurrentlyFlagged) {
      newFlagged?.delete(reflectionId);
    } else {
      newFlagged?.add(reflectionId);
    }
    setFlaggedReflections(newFlagged);

    try {
      if (isCurrentlyFlagged) {
        await supabase?.from('verse_reflection_flags')?.delete()
          ?.eq('user_id', user?.id)?.eq('reflection_id', reflectionId);
        showSuccess('Flag removed.');
      } else {
        await supabase?.from('verse_reflection_flags')?.upsert(
          { user_id: user?.id, reflection_id: reflectionId, reason: 'user_report' },
          { onConflict: 'user_id,reflection_id' }
        );
        showSuccess('Reflection flagged for admin review. Thank you.');
      }
    } catch (err) {
      setFlaggedReflections(flaggedReflections);
      console.error('Error toggling flag:', err);
    }
  };

  const getLikeCount = (reflection) => {
    const baseLikes = reflection?.likes || 0;
    const userLiked = likedReflections?.has(reflection?.id);
    const wasOriginallyLiked = reflection?.userHasLiked || false;
    if (userLiked && !wasOriginallyLiked) return baseLikes + 1;
    if (!userLiked && wasOriginallyLiked) return Math.max(0, baseLikes - 1);
    return baseLikes;
  };

  const isLikedByUser = (reflection) => likedReflections?.has(reflection?.id) || reflection?.userHasLiked;
  const isFlaggedByUser = (reflection) => flaggedReflections?.has(reflection?.id) || reflection?.userHasFlagged;

  const heatmapBadge = getHeatmapBadge(allReflections?.length);

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50 z-40" onClick={onClose} />
      
      {/* Modal */}
      <div className={`fixed inset-4 md:inset-8 lg:inset-16 bg-card rounded-lg shadow-warm-lg z-50 flex flex-col ${className}`}>
        {/* Header */}
        <div className="flex items-start justify-between p-6 border-b border-border">
          <div className="flex-1 mr-4">
            <div className="flex items-center space-x-2 mb-2">
              <Icon name="BookOpen" size={20} className="text-primary" />
              <h2 className="text-xl font-heading font-semibold text-foreground">Verse Reflections</h2>
              <div className={`px-2 py-1 rounded-full text-xs font-medium ${heatmapBadge?.color}`}>
                {heatmapBadge?.label}
              </div>
            </div>
            
            <div className="bg-muted/30 p-4 rounded-lg mb-3">
              <h3 className="font-medium text-foreground mb-1">{verse?.reference}</h3>
              <p className="text-sm text-foreground/80 leading-relaxed">"{verse?.text}"</p>
              <p className="text-xs text-muted-foreground mt-2">— {verse?.version || 'KJV'}</p>
            </div>
            
            <div className="flex items-center space-x-4 text-sm">
              <div className="flex items-center space-x-1">
                <Icon name="Users" size={14} className="text-muted-foreground" />
                <span className="text-muted-foreground">
                  {allReflections?.length} reflection{allReflections?.length !== 1 ? 's' : ''}
                </span>
              </div>
              {publicReflections?.length > 0 && (
                <div className="flex items-center space-x-1">
                  <Icon name="Globe" size={12} className="text-muted-foreground" />
                  <span className="text-muted-foreground">{publicReflections?.length} public</span>
                </div>
              )}
            </div>
          </div>
          
          <Button variant="ghost" size="icon" onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Tabs */}
        <div className="border-b border-border px-6">
          <div className="flex space-x-4">
            <button
              onClick={() => setSelectedTab('all')}
              className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                selectedTab === 'all' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              All ({allReflections?.length})
            </button>
            {publicReflections?.length > 0 && (
              <button
                onClick={() => setSelectedTab('public')}
                className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200 flex items-center space-x-1 ${
                  selectedTab === 'public' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon name="Globe" size={12} />
                <span>Public ({publicReflections?.length})</span>
              </button>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {displayedReflections?.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <Icon name="MessageSquare" size={40} className="text-muted-foreground/40 mb-3" />
              <p className="text-muted-foreground font-medium mb-1">No reflections yet</p>
              <p className="text-sm text-muted-foreground/70">Be the first to share your thoughts on this verse.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {displayedReflections?.map((reflection) => (
                <div key={reflection?.id} className="bg-background border border-border rounded-lg p-4">
                  {/* Author + date */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center">
                        <Icon name="User" size={14} className="text-primary" />
                      </div>
                      <span className="text-sm font-medium text-foreground">{reflection?.author || 'Anonymous'}</span>
                    </div>
                    <span className="text-xs text-muted-foreground">{formatDate(reflection?.createdAt)}</span>
                  </div>

                  {/* Reflection content */}
                  <div className="space-y-2">
                    {prompts?.map(prompt => {
                      const content = reflection?.[prompt?.id];
                      if (!content) return null;
                      return (
                        <div key={prompt?.id} className="text-sm">
                          <div className="flex items-center gap-1.5 mb-0.5">
                            <Icon name={prompt?.icon} size={12} className="text-primary" />
                            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{prompt?.title}</span>
                          </div>
                          <p className="text-foreground/80 leading-relaxed pl-4">{content}</p>
                        </div>
                      );
                    })}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-3 mt-3 pt-3 border-t border-border/50">
                    <button
                      onClick={() => handleLikeReflection(reflection?.id)}
                      disabled={likeSaving === reflection?.id}
                      className={`flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border transition-colors ${
                        isLikedByUser(reflection)
                          ? 'border-primary/40 bg-primary/10 text-primary' :'border-border text-muted-foreground hover:border-primary/30 hover:text-primary'
                      }`}
                    >
                      <Icon name="Heart" size={12} />
                      <span>{getLikeCount(reflection)}</span>
                    </button>
                    <button
                      onClick={() => handleFlagReflection(reflection?.id)}
                      className={`flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border transition-colors ${
                        isFlaggedByUser(reflection)
                          ? 'border-destructive/40 bg-destructive/10 text-destructive' :'border-border text-muted-foreground hover:border-destructive/30 hover:text-destructive'
                      }`}
                    >
                      <Icon name="Flag" size={12} />
                      <span>{isFlaggedByUser(reflection) ? 'Flagged' : 'Flag'}</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default VerseReflectionsViewModal;